// A single, positioned scene is consumed by the screen, PDF and editable PPTX.
// Coordinates and line breaks are fixed before export; no renderer repaginates.
type AdvisorSceneNode = { kind: 'text' | 'rect' | 'line' | 'path'; x: number; y: number; w?: number; h?: number;
  text?: string; size?: number; bold?: boolean; color: string; fill?: string; points?: number[][]; align?: string; radius?: number }
type AdvisorScenePage = { id: string; sectionId: string; title: string; continued: boolean; dark: boolean; nodes: AdvisorSceneNode[] }
type AdvisorScene = { version: number; width: number; height: number; font: string; title: string; periodLabel: string; pages: AdvisorScenePage[] }
const advisorPresentationApi = (() => {
  const W = 1280, H = 720, LEFT = 64, RIGHT = 1216, TOP = 148, BOTTOM = 658;
  const colors = { ink: '#251739', muted: '#726880', purple: '#7540ed', pale: '#f5f1fb', line: '#ded5ed', green: '#21815e', red: '#c64756', amber: '#a86c16' };
  const safe = (value: unknown) => String(value ?? '').replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f]/g, '');
  const measure = (value: string, size: number, bold = false) => {
    if (typeof document !== 'undefined') {
      const context = document.createElement('canvas').getContext('2d');
      if (context) { context.font = `${bold ? 800 : 400} ${size}px Nunito`; return context.measureText(value).width; }
    }
    return value.length * size * .57;
  };
  const wrap = (value: unknown, width: number, size: number, bold = false): string[] => {
    const lines: string[] = [];
    for (const paragraph of safe(value).split('\n')) {
      let line = '';
      for (let word of paragraph.split(/\s+/).filter(Boolean)) {
        if (line && measure(`${line} ${word}`, size, bold) > width) { lines.push(line); line = ''; }
        while (measure(word, size, bold) > width && word.length > 1) {
          let end = 1;
          while (end < word.length && measure(word.slice(0, end + 1), size, bold) <= width) end++;
          lines.push(word.slice(0, end)); word = word.slice(end);
        }
        line += (line ? ' ' : '') + word;
      }
      lines.push(line);
    }
    return lines;
  };
  const text = (page: AdvisorScenePage, value: unknown, x: number, y: number, width: number, size = 22, opts: any = {}) => {
    const lines = wrap(value, width, size, opts.bold);
    lines.forEach((line, index) => page.nodes.push({ kind: 'text', text: line, x, y: y + index * size * 1.4,
      w: width, h: size * 1.4, size, color: page.dark ? '#ffffff' : colors.ink, ...opts }));
    return lines.length * size * 1.4;
  };
  const rect = (page: AdvisorScenePage, x: number, y: number, w: number, h: number, color: string, radius = 0) =>
    page.nodes.push({ kind: 'rect', x, y, w, h, color, radius });
  const line = (page: AdvisorScenePage, x: number, y: number, w: number, h: number, color = colors.line) =>
    page.nodes.push({ kind: 'line', x, y, w, h, color });
  const build = (input: any): AdvisorScene => {
    const labels = input.labels || {};
    const pages: AdvisorScenePage[] = [];
    const sections = input.sections || [];
    const create = (sectionId: string, title: string, continued = false, dark = false): AdvisorScenePage => {
      const page: AdvisorScenePage = { id: `${sectionId}-${pages.length}`, sectionId, title, continued, dark, nodes: [] };
      rect(page, 0, 0, W, H, dark ? '#5930ae' : colors.pale);
      if (dark) {
        page.nodes.push({ kind: 'path', x: 0, y: 0, color: '#6840c1', fill: '#6840c1', points: [[500,0],[900,0],[180,720],[0,720],[0,500],[500,0]] });
        page.nodes.push({ kind: 'path', x: 0, y: 0, color: '#7540ed', fill: '#7540ed', points: [[1180,0],[1280,0],[1280,720],[460,720],[1180,0]] });
      }
      pages.push(page);
      if (sectionId !== 'cover') {
        const number = sections.findIndex((s: any) => s.id === sectionId) + 1;
        text(page, `${number > 0 ? String(number).padStart(2, '0') + ' · ' : ''}${labels.brand || 'PLUMME'}`, LEFT, 42, 1100, 14,
          { bold: true, color: dark ? '#e6d9ff' : colors.purple });
        text(page, title + (continued ? ` · ${labels.continued || ''}` : ''), LEFT, 76, RIGHT - LEFT, 30, { bold: true });
      }
      return page;
    };
    const cover = create('cover', input.title, false, true);
    text(cover, labels.brand || 'PLUMME', LEFT, 58, 700, 20, { bold: true });
    text(cover, input.businessName || '', 800, 58, 416, 17, { align: 'right' });
    text(cover, labels.presentation || '', LEFT, 236, 1100, 16, { color: '#e6d9ff' });
    text(cover, input.title, LEFT, 282, 1120, 50, { bold: true });
    text(cover, input.periodLabel, LEFT, 466, 1100, 26);
    text(cover, labels.coverFoot || '', LEFT, 610, 1100, 16, { color: '#e6d9ff' });
    let page = create('contents', labels.contents || '', false);
    sections.forEach((section: any, index: number) => {
      const local = index % 20;
      if (index && !local) page = create('contents', labels.contents || '', true);
      const x = local < 10 ? LEFT : 672;
      const y = TOP + (local % 10) * 49;
      text(page, String(index + 1).padStart(2, '0'), x, y, 45, 18, { bold: true, color: colors.purple });
      text(page, section.title, x + 52, y, 490, 18, { bold: true });
      line(page, x, y + 40, 540, 0);
    });
    sections.forEach((section: any) => {
      let current = create(section.id, section.title, false, section.id === 'risiko');
      let y = TOP;
      const next = () => { current = create(section.id, section.title, true, section.id === 'risiko'); y = TOP; };
      const room = (height: number) => { if (y + height > BOTTOM && y > TOP) next(); };
      const api = { get page() { return current; }, get y() { return y; }, set y(value: number) { y = value; }, next, room,
        sectionId: section.id, actionColumns: labels.actionColumns || ['', '', ''], locale: input.locale, text, rect, line, wrap, measure, colors, LEFT, RIGHT, TOP, BOTTOM };
      const blocks = [...(section.blocks || [])];
      if (section.id === 'sammendrag') {
        const kpis = sections.find((s: any) => s.id === 'hovedtall')?.blocks.find((b: any) => b.sourceType === 'kpis');
        const chartIndex = blocks.findIndex((b: any) => b.kind === 'chart');
        if (kpis && chartIndex >= 0) {
          const [chart] = blocks.splice(chartIndex, 1);
          blocks.unshift({ kind: 'overview', rows: kpis.rows, chart });
        } else if (kpis) blocks.unshift(kpis);
      }
      window.PlummeAdvisorPresentationBlocks.render(blocks, api);
    });
    // Join related sections only when both fit, retaining each section heading.
    // Interactive actions/decisions keep their own page and section identity.
    const pairs = new Set(['endring/hendelser', 'likviditet/arbeidskapital', 'utvikling/leverandorer']);
    const body = (p: AdvisorScenePage) => p.nodes.filter((n) => n.kind === 'path'
      ? n.points?.every((point) => point[1] >= TOP) : n.y >= TOP);
    const bottom = (nodes: AdvisorSceneNode[]) => Math.max(TOP, ...nodes.map((n) => n.kind === 'path'
      ? Math.max(...(n.points || []).map((point) => point[1])) : n.y + (n.h || 0)));
    for (let i = 2; i < pages.length - 1; i++) {
      const first = pages[i], second = pages[i + 1];
      if (!pairs.has(`${first.sectionId}/${second.sectionId}`) || first.continued || second.continued
        || pages.filter((p) => p.sectionId === first.sectionId || p.sectionId === second.sectionId).length !== 2) continue;
      const firstBottom = bottom(body(first)), secondBody = body(second);
      const headingHeight = wrap(second.title, RIGHT - LEFT, 22, true).length * 30.8;
      const offset = firstBottom + 38 + headingHeight - TOP;
      if (bottom(secondBody) + offset > BOTTOM) continue;
      line(first, LEFT, firstBottom + 14, RIGHT - LEFT, 0);
      text(first, second.title, LEFT, firstBottom + 26, RIGHT - LEFT, 22, { bold: true });
      first.nodes.push(...secondBody.map((n) => n.kind === 'path'
        ? { ...n, points: n.points?.map(([x, y]) => [x, y + offset]) }
        : { ...n, y: n.y + offset }));
      first.title += ' / ' + second.title;
      pages.splice(i + 1, 1);
    }
    pages.forEach((p, index) => {
      const color = p.dark ? '#e6d9ff' : colors.muted;
      text(p, [input.businessName, input.periodLabel].filter(Boolean).join(' · '), LEFT, 687, 1050, 13, { color });
      text(p, `${index + 1} / ${pages.length}`, 1140, 687, 76, 13, { color, align: 'right' });
    });
    return { version: 1, width: W, height: H, font: 'Nunito', title: input.title, periodLabel: input.periodLabel, pages };
  };
  return { build, wrap, measure, colors };
})();
Object.assign(window, { PlummeAdvisorPresentation: advisorPresentationApi });
